import React, { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useLanguage } from '@/context/LanguageContext';
import { api } from '@/utils/api';
import type { MaintenanceStatus } from '@/hooks/useMaintenanceStatus';

interface MaintenanceLoginProps {
    onSuccess: () => void;
}

/**
 * Compact staff-access login card shown in the top-right corner of the
 * maintenance overlay. Delegates to authStore.login() — no duplicated
 * auth logic. On success the parent re-checks status immediately; if the
 * account is still not allowed (not admin / not allowlisted) the form
 * stays with an "Access restricted" message.
 */
export function MaintenanceLogin({ onSuccess }: MaintenanceLoginProps) {
    const { t } = useLanguage();
    const login = useAuthStore((state) => state.login);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            await login(email, password);
            // Re-check the status with the new token. Only surface the
            // restricted message when the account is genuinely still locked
            // out (not admin / not allowlisted) — a successful login for an
            // allowed account must not flash a false error.
            const response = await api.get('/v1/public/maintenance-status');
            const status = response.data as MaintenanceStatus;
            if (status.active && !status.allowed) {
                setError(t('maintenance.access_restricted'));
            }
            // Parent re-fetches status so the overlay unmounts when allowed.
            onSuccess();
        } catch (err: unknown) {
            // Login failed — surface the real reason (wrong password,
            // unverified email, 2FA required, etc.) instead of a generic
            // "Access restricted" which implies an allowlist denial.
            const e = err as { response?: { data?: { message?: string } } };
            setError(e.response?.data?.message || t('maintenance.login_failed'));
            setPassword('');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="maintenance-login" aria-label={t('maintenance.login_title')}>
            <h2 className="text-sm font-semibold text-white mb-3">{t('maintenance.login_title')}</h2>
            <div className="space-y-2">
                <input
                    type="email"
                    required
                    autoComplete="email"
                    placeholder={t('maintenance.login_email')}
                    aria-label={t('maintenance.login_email')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-md border border-white/20 bg-white/10 px-3 py-2 text-sm text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40"
                />
                <input
                    type="password"
                    required
                    autoComplete="current-password"
                    placeholder={t('maintenance.login_password')}
                    aria-label={t('maintenance.login_password')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-md border border-white/20 bg-white/10 px-3 py-2 text-sm text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40"
                />
                <button
                    type="submit"
                    disabled={submitting}
                    className="w-full rounded-md bg-white/90 px-3 py-2 text-sm font-semibold text-black hover:bg-white disabled:opacity-50"
                >
                    {t('maintenance.login_submit')}
                </button>
            </div>
            {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
        </form>
    );
}
